import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Filter as FilterIcon, Plus, X } from "lucide-react";
import { FilterValuePicker } from "@/components/bi/FilterValuePicker";

const OPS = [
  { value: "eq", label: "=" },
  { value: "neq", label: "≠" },
  { value: "contains", label: "contains" },
  { value: "in", label: "in (list)" },
  { value: "not_in", label: "not in" },
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
  { value: "between", label: "between" },
];

const NUM_RE = /^-?\d+(\.\d+)?$/;
const smart = (s) => (NUM_RE.test(String(s).trim()) ? Number(s) : String(s).trim());

function parseValue(op, raw) {
  if (op === "in" || op === "not_in") return String(raw).split(",").map((x) => smart(x)).filter((x) => x !== "");
  if (op === "between") {
    const parts = String(raw).split(",").map((x) => smart(x));
    return [parts[0] ?? "", parts[1] ?? ""];
  }
  if (op === "contains") return String(raw);
  return smart(raw);
}

function displayValue(v) {
  if (Array.isArray(v)) return v.join(", ");
  return v == null ? "" : String(v);
}

// Filters that apply ONLY to this widget (stored in config.filters, merged into every query)
export function WidgetFiltersEditor({ config, fields, disp, patchConfig, datasetId = null }) {
  const filters = config.filters || [];
  const add = () => patchConfig({ filters: [...filters, { field: fields[0]?.name || "", op: "eq", value: "" }] });
  const upd = (i, patch) => {
    const arr = [...filters];
    arr[i] = { ...arr[i], ...patch };
    patchConfig({ filters: arr });
  };
  const rm = (i) => { const arr = [...filters]; arr.splice(i, 1); patchConfig({ filters: arr }); };

  return (
    <div data-testid="widget-filters-editor" className="pt-1 border-t border-[color:var(--glass-border)]">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><FilterIcon className="w-3.5 h-3.5" />Filters on this visual</Label>
        <Button data-testid="add-widget-filter" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={add}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {filters.length > 0 && <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Applied only to this widget, on top of dashboard filters.</p>}
      <div className="space-y-2 mt-1.5">
        {filters.map((f, i) => (
          <div key={i} data-testid={`widget-filter-row-${i}`} className="rounded-lg bg-[color:var(--surface-2)] p-1.5 space-y-1.5">
            <div className="flex gap-1.5 items-center">
              <Select value={f.field || ""} onValueChange={(v) => upd(i, { field: v })}>
                <SelectTrigger data-testid={`wf-field-${i}`} className="bg-[color:var(--surface)] flex-1 min-w-0 h-8 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
                <SelectContent>{fields.map((c) => <SelectItem key={c.name} value={c.name}>{disp ? disp(c.name) : c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={f.op || "eq"} onValueChange={(v) => upd(i, { op: v, value: parseValue(v, displayValue(f.value)) })}>
                <SelectTrigger data-testid={`wf-op-${i}`} className="bg-[color:var(--surface)] w-[92px] shrink-0 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{OPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-[color:var(--ink-3)]" onClick={() => rm(i)}><X className="w-3.5 h-3.5" /></Button>
            </div>
            {(f.op === "in" || f.op === "not_in") && datasetId && f.field ? (
              <FilterValuePicker datasetId={datasetId} field={f.field} testid={`wf-picker-${i}`}
                value={Array.isArray(f.value) ? f.value : []} exclude={f.op === "not_in"}
                onApply={(vals, ex) => upd(i, { op: ex ? "not_in" : "in", value: vals })} />
            ) : (
              <Input data-testid={`wf-value-${i}`} className="h-8 text-xs bg-[color:var(--surface)]" value={displayValue(f.value)}
                placeholder={f.op === "in" || f.op === "not_in" ? "a, b, c" : f.op === "between" ? "low, high" : "value"}
                onChange={(e) => upd(i, { value: parseValue(f.op || "eq", e.target.value) })} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
